import { useState, useMemo, useCallback } from 'react';
import type { RepositoryItem } from './types';
import { generateId } from './repositoryUtils';
import { useCopyToClipboard } from '../../../../hooks';

export interface UseRepositoryItemsOptions<T extends RepositoryItem> {
  /** Widget config containing at least { dataKey: string } */
  config: { dataKey: string };
  /** Dashboard data object keyed by dataKey */
  dashboardData?: Record<string, unknown>;
  /** Callback to persist changes */
  onDataChange?: (dataKey: string, data: unknown) => void;
  /** Key in the data content that holds the items array (e.g. 'images', 'notes', 'pdfs') */
  itemsKey: string;
  /** Prefix for generated IDs (e.g. 'img', 'note', 'pdf') */
  idPrefix: string;
  /** Fields to search across (e.g. ['title', 'description', 'tags']) */
  searchFields: (keyof T)[];
}

export interface UseRepositoryItemsResult<T extends RepositoryItem> {
  /** All items in the repository */
  items: T[];
  /** Raw repository data content object */
  repoData: Record<string, unknown> | undefined;
  /** Current search query */
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  /** Items filtered by search query */
  filteredItems: T[];
  /** ID of the item whose ref was just copied (for UI feedback) */
  copiedId: string | null;
  /** Copy a ref:// link to clipboard */
  handleCopyRef: (id: string, e?: React.MouseEvent) => void;
  /** Create a new item with auto-generated id and timestamps */
  createItem: (newItem: Omit<T, 'id' | 'createdAt' | 'updatedAt'>) => void;
  /** Update fields on an existing item by id */
  updateItem: (id: string, updates: Partial<T>) => void;
  /** Request deletion (sets deletingItemId for confirmation UI) */
  deleteItem: (id: string) => void;
  /** ID of item pending deletion confirmation */
  deletingItemId: string | null;
  setDeletingItemId: (id: string | null) => void;
  /** Execute the pending deletion */
  confirmDelete: () => void;
}

/**
 * Shared hook for repository widget CRUD, search, and ref-copy operations.
 * Handles state management for create/update/delete and search filtering.
 */
export function useRepositoryItems<T extends RepositoryItem>(
  options: UseRepositoryItemsOptions<T>,
): UseRepositoryItemsResult<T> {
  const { config, dashboardData = {}, onDataChange, itemsKey, idPrefix, searchFields } = options;

  const [searchQuery, setSearchQuery] = useState('');
  const { copiedId, copy } = useCopyToClipboard();
  const [deletingItemId, setDeletingItemId] = useState<string | null>(null);

  // Extract items from dashboard data
  const repoData = dashboardData[config.dataKey] as Record<string, unknown> | undefined;
  const items = (repoData?.[itemsKey] as T[] | undefined) || [] as T[];

  // Filter items by search query
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return items;
    const query = searchQuery.toLowerCase();
    return items.filter(item =>
      searchFields.some(field => {
        const value = item[field];
        if (Array.isArray(value)) {
          return value.some(v => String(v).toLowerCase().includes(query));
        }
        return value != null && String(value).toLowerCase().includes(query);
      })
    );
  }, [items, searchQuery, searchFields]);

  // Copy reference link
  const handleCopyRef = useCallback((id: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    copy(`ref://${id}`, id);
  }, [copy]);

  // Create a new item
  const createItem = useCallback((newItem: Omit<T, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (!onDataChange) return;
    const now = new Date().toISOString();
    const item = {
      ...newItem,
      id: generateId(idPrefix),
      createdAt: now,
      updatedAt: now,
    } as T;

    const updatedData = {
      ...repoData,
      [itemsKey]: [...items, item],
    };
    onDataChange(config.dataKey, updatedData);
  }, [onDataChange, repoData, items, itemsKey, idPrefix, config.dataKey]);

  // Update an existing item
  const updateItem = useCallback((id: string, updates: Partial<T>) => {
    if (!onDataChange) return;
    const updatedItems = items.map(item =>
      item.id === id
        ? { ...item, ...updates, updatedAt: new Date().toISOString() }
        : item
    );
    const updatedData = {
      ...repoData,
      [itemsKey]: updatedItems,
    };
    onDataChange(config.dataKey, updatedData);
  }, [onDataChange, repoData, items, itemsKey, config.dataKey]);

  // Request deletion (for confirmation UI)
  const deleteItem = (id: string) => {
    setDeletingItemId(id);
  };

  // Execute the pending deletion
  const confirmDelete = useCallback(() => {
    if (!onDataChange || !deletingItemId) return;
    const updatedData = {
      ...repoData,
      [itemsKey]: items.filter(item => item.id !== deletingItemId),
    };
    onDataChange(config.dataKey, updatedData);
    setDeletingItemId(null);
  }, [onDataChange, deletingItemId, repoData, items, itemsKey, config.dataKey]);

  return {
    items,
    repoData,
    searchQuery,
    setSearchQuery,
    filteredItems,
    copiedId,
    handleCopyRef,
    createItem,
    updateItem,
    deleteItem,
    deletingItemId,
    setDeletingItemId,
    confirmDelete,
  };
}
